import React, { useEffect, useState } from "react";
import { Button, Modal, Input, Space } from "antd";
import { useLinkProfile } from "../api/useLinkProfile";
import { LinkProfile } from "../models";

const defaultButtonStyles = {
  fontSize: "1.5rem",
  color: "#000000",
  backgroundColor: "rgba(16, 185, 129, 0.5)",
};

interface Props {
  profileIn: LinkProfile;
}

export const PageEditor: React.FC<Props> = ({ profileIn }) => {
  const { updateLinkProfile } = useLinkProfile();
  const [profile, setProfile] = useState<LinkProfile>(profileIn);

  // Video URL logic from DymanicOverlay
  let videoURL: string | null = null;
  if (profile?.video_link) {
    const url = new URL(profile.video_link);
    url.searchParams.set("autoplay", "1");
    url.searchParams.set("loop", "1");
    url.searchParams.set("rel", "0");
    url.searchParams.set("native_context_menu", "0");
    url.searchParams.set("controls", "0");
    url.searchParams.set("progress_bar", "0");
    url.searchParams.set("fullscreen_button", "0");
    url.searchParams.set("timestamp", "0");
    url.searchParams.set("play_button", "0");
    url.searchParams.set("volume_control", "0");
    url.searchParams.set("closed_caption", "0");
    videoURL = url.toString();
  }

  useEffect(() => {
    setProfile(profileIn);
    setButtonStyles({
      fontSize: defaultButtonStyles.fontSize,
      color: profileIn.text_color || defaultButtonStyles.color,
      backgroundColor:
        profileIn.background_color || defaultButtonStyles.backgroundColor,
    });
  }, [profileIn]);

  const [showDialog, setShowDialog] = useState(false);
  const [buttonStyles, setButtonStyles] = useState(defaultButtonStyles);

  const handleOpenDialog = () => {
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
  };

  const handleApply = () => {
    updateLinkProfile({
      ...profile,
      text_color: buttonStyles.color,
      background_color: buttonStyles.backgroundColor,
    });

    setShowDialog(false);
  };

  if (!profileIn.id) {
    return <div className="bg-black w-full h-full">Preview Panel</div>;
  }

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        backgroundColor: "#000",
      }}
    >
      {/* Video background */}
      {videoURL && (
        <iframe
          src={videoURL}
          allowFullScreen={false}
          title="Profile Video"
          style={{
            width: "100%",
            height: "100%",
            zIndex: 0,
            pointerEvents: "none",
            background: "black",
          }}
          allow="autoplay; encrypted-media;"
        />
      )}

      {/* Buttons overlay */}
      <div
        style={{
          width: "100%",
          height: "100%",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Button
          className="order-button"
          style={{
            position: "absolute",
            width: "75%",
            bottom: "max(2rem, 4vh)",
            height: "clamp(3rem, 8vh, 4rem)",
            backgroundColor: buttonStyles.backgroundColor,
            color: buttonStyles.color,
            fontSize: buttonStyles.fontSize,
            fontWeight: "bold",
            paddingLeft: "clamp(1rem, 3vw, 1.5rem)",
            paddingRight: "clamp(1rem, 3vw, 1.5rem)",
            paddingTop: "clamp(1rem, 3vw, 1.5rem)",
            paddingBottom: "clamp(1rem, 3vw, 1.5rem)",
            borderRadius: "9px",
            boxShadow:
              "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
            transition: "all 300ms",
            pointerEvents: "auto",
            border: "none",
            zIndex: 2,
          }}
          onClick={handleOpenDialog}
          onMouseOver={(e) => {
            // Try to brighten the color (works for hex and rgb(a))
            let bg = buttonStyles.backgroundColor;
            let bright = bg;
            if (bg.startsWith("#")) {
              // hex color
              const hex = bg.replace("#", "");
              if (hex.length === 3 || hex.length === 6) {
                let num = parseInt(
                  hex.length === 3
                    ? hex
                        .split("")
                        .map((x) => x + x)
                        .join("")
                    : hex,
                  16
                );
                let r = Math.min(255, ((num >> 16) & 0xff) + 40);
                let g = Math.min(255, ((num >> 8) & 0xff) + 40);
                let b = Math.min(255, (num & 0xff) + 40);
                bright = `rgb(${r},${g},${b})`;
              }
            } else if (bg.startsWith("rgb")) {
              // rgb or rgba
              const nums = bg.match(/[\d.]+/g);
              if (nums) {
                let [r, g, b, a] = nums.map(Number);
                r = Math.min(255, r + 40);
                g = Math.min(255, g + 40);
                b = Math.min(255, b + 40);
                bright =
                  a !== undefined
                    ? `rgba(${r},${g},${b},${a})`
                    : `rgb(${r},${g},${b})`;
              }
            }
            e.currentTarget.style.backgroundColor = bright;
          }}
          onMouseOut={(e) =>
            (e.currentTarget.style.backgroundColor =
              buttonStyles.backgroundColor)
          }
        >
          {profile?.guide_text || "Order Now"}
        </Button>
        <Button
          className="about-button"
          style={{
            position: "absolute",
            top: "calc(100% + 40px)",
            right: 0,
            backgroundColor: "rgba(59, 130, 246, 0.4)",
            color: "white",
            padding: "8px 12px",
            borderRadius: "4px",
            fontSize: "clamp(12px, 1.5vw, 14px)",
            whiteSpace: "nowrap",
            border: "1px solid rgba(255, 255, 255, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 300ms",
            pointerEvents: "auto",
            zIndex: 2,
            marginBottom: 12,
          }}
          onClick={() =>
            profile?.about_link && (window.location.href = profile.about_link)
          }
          onMouseOver={(e) =>
            (e.currentTarget.style.backgroundColor = "rgba(59, 130, 246, 0.9)")
          }
          onMouseOut={(e) =>
            (e.currentTarget.style.backgroundColor = "rgba(59, 130, 246, 0.4)")
          }
        >
          {profile?.about_text || "About"}
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{ marginLeft: "4px" }}
          >
            <path
              d="M5 12H19"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M12 5L19 12L12 19"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </Button>
        <Modal
          title="Edit Order Button Style"
          open={showDialog}
          onCancel={handleCloseDialog}
          onOk={handleApply}
          okText="Apply"
          cancelText="Cancel"
        >
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            {/* <div>
              <label style={{ fontWeight: 500 }}>Font Size (e.g. 1.5rem, 24px)</label>
              <Input
                value={tempStyles.fontSize}
                onChange={e => setTempStyles({ ...tempStyles, fontSize: e.target.value })}
                placeholder="Font size"
                style={{ marginTop: 4 }}
              />
            </div> */}
            <div>
              <label style={{ fontWeight: 500 }}>Text Color</label>
              <Input
                type="color"
                value={buttonStyles.color}
                onChange={(e) =>
                  setButtonStyles({ ...buttonStyles, color: e.target.value })
                }
                style={{
                  width: 60,
                  height: 36,
                  marginLeft: 12,
                  verticalAlign: "middle",
                }}
              />
            </div>
            <div>
              <label style={{ fontWeight: 500 }}>Background Color</label>
              <Input
                type="color"
                value={
                  buttonStyles.backgroundColor.startsWith("rgba")
                    ? "#10b981"
                    : buttonStyles.backgroundColor
                }
                onChange={(e) =>
                  setButtonStyles({
                    ...buttonStyles,
                    backgroundColor: e.target.value,
                  })
                }
                style={{
                  width: 60,
                  height: 36,
                  marginLeft: 12,
                  verticalAlign: "middle",
                }}
              />
            </div>
          </Space>
          <div style={{ marginTop: 20 }}>
            <label style={{ fontWeight: 500 }}>Order Link</label>
            <Input
              value={profile?.guide_link || ""}
              onChange={(e) => {
                if (profile) {
                  setProfile({ ...profile, guide_link: e.target.value });
                }
              }}
              placeholder="Enter Order link"
              style={{ marginTop: 4 }}
            />
          </div>
        </Modal>
      </div>
    </div>
  );
};
